import type { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Stack } from "@volca/ui/components/ui/stack";
import type { StAdjustmentTechnicianGroup } from "@/payroll/pay-breakdown/st-adjustments/types";
import { isPushableGroup } from "@/payroll/pay-breakdown/st-adjustments/utils";
import type { TechnicianSelectionColumnOptions } from "./types";

export const SelectCol = ({
  disabled,
  selectedTechnicianIds,
  allPushableSelected,
  somePushableSelected,
  pushableCount,
  onToggleAll,
  onToggle,
}: TechnicianSelectionColumnOptions): ColumnDef<
  StAdjustmentTechnicianGroup,
  unknown
> => ({
  id: "select",
  enableSorting: false,
  meta: { size: "3.5rem" },
  // stopPropagation on both header and cell: the row's onRowClick opens the
  // drill-down sidebar, and ticking a checkbox is a different intent.
  header: () => (
    <Stack
      gap={4}
      align="center"
      justify="center"
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        checked={allPushableSelected}
        indeterminate={!allPushableSelected && somePushableSelected}
        disabled={disabled || pushableCount === 0}
        onCheckedChange={(checked) => onToggleAll(checked === true)}
        aria-label="Select all technicians"
      />
    </Stack>
  ),
  cell: ({ row }) => {
    const group = row.original;
    // Selection pruning runs in an effect, so for one render a technician whose
    // last line just landed is still in the set while the row is already
    // disabled. Reading eligibility here keeps the tick from outliving it.
    const pushable = isPushableGroup(group);

    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={pushable && selectedTechnicianIds.has(group.employee_id)}
          disabled={disabled || !pushable}
          onCheckedChange={(checked) => onToggle(group, checked === true)}
          aria-label={
            group.employee_id === undefined
              ? "Select unknown technician"
              : `Select technician ${group.employee_id}`
          }
        />
      </Stack>
    );
  },
});
